// src/lib/exportExcel.ts
import ExcelJS from 'exceljs';

export type XlCatch={name:string;area:number;surface:string;C:number;H:number;L:number};
export type XlLinkRow={label:string;pipe:boolean;size:number;us:string;ds:string;len:number;
  usIL:number;dsIL:number;nPipes:number;usGL:number;dsGL:number;
  material:string;condition:string;
  catchAllocs:{name:string;pct:number}[]; upstream:string[]};
export type XlInfo={job:string;jobNo:string;by:string;subject:string;
  climateWording:string;returnPeriod:number;stormTableTitle:string;
  manningMat:string;manningCond:string;cwMat:string;cwCond:string};

const sanitize=(s:string)=>(s.replace(/[\[\]\*\?\/\\:]/g,'-').trim().slice(0,31))||'X';
const numSort=(a:any,b:any)=>parseInt(a.name.replace(/\D/g,''))-parseInt(b.name.replace(/\D/g,''));
const trimRows=(ws:ExcelJS.Worksheet,fromRow:number,lastTemplateRow:number)=>{
  const n=lastTemplateRow-fromRow+1; if(n>0)ws.spliceRows(fromRow,n);};
const download=(buf:ArrayBuffer,name:string)=>{
  const blob=new Blob([buf],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
  const url=URL.createObjectURL(blob);const a=document.createElement('a');
  a.href=url;a.download=name;document.body.appendChild(a);a.click();a.remove();
  setTimeout(()=>URL.revokeObjectURL(url),1000);};

export async function exportExcel(info:XlInfo,rows:XlLinkRow[],catches:XlCatch[],
  templateUrl='/template.xlsx',baseName='drainage-check'):Promise<{files:number;warnings:string[]}>{
  const warnings:string[]=[];
  const buf=await(await fetch(templateUrl)).arrayBuffer();
  const probe=new ExcelJS.Workbook(); await probe.xlsx.load(buf);
  const maxUC=probe.worksheets.filter(w=>/^UC\d+$/.test(w.name)).length;
  const maxP =probe.worksheets.filter(w=>/^P\d+$/.test(w.name)).length;

  // chunk in drawing order so each file fits the template
  const chunks:XlLinkRow[][]=[]; let cur:XlLinkRow[]=[],uc=0,pp=0;
  for(const r of rows){
    if((r.pipe?pp+1:pp)>maxP||(r.pipe?uc:uc+1)>maxUC){chunks.push(cur);cur=[];uc=0;pp=0;}
    cur.push(r); r.pipe?pp++:uc++;}
  if(cur.length)chunks.push(cur);

  for(let ci=0;ci<chunks.length;ci++){
    const wb=new ExcelJS.Workbook(); await wb.xlsx.load(buf.slice(0));
    fillWorkbook(wb,info,chunks[ci],catches,warnings);
    const out=await wb.xlsx.writeBuffer();
    download(out as ArrayBuffer,chunks.length>1?`${baseName}-part${ci+1}.xlsx`:`${baseName}.xlsx`);
  }
  return {files:chunks.length,warnings};
}

function fillWorkbook(wb:ExcelJS.Workbook,info:XlInfo,rows:XlLinkRow[],
  catches:XlCatch[],warnings:string[]){
  const chunkLabels=new Set(rows.map(r=>r.label));

  // ---- Info ----
  const wsI=wb.getWorksheet('Info')!;
  wsI.getCell('Q2').value=info.job;      wsI.getCell('AB2').value=info.jobNo;
  wsI.getCell('X4').value=info.by;       wsI.getCell('F4').value=info.subject;
  wsI.getCell('E39').value=info.climateWording;
  wsI.getCell('T41').value=info.returnPeriod;
  wsI.getCell('H42').value=info.stormTableTitle;
  wsI.getCell('I84').value=info.manningMat; wsI.getCell('I85').value=info.manningCond;
  wsI.getCell('I94').value=info.cwMat;      wsI.getCell('I95').value=info.cwCond;

  // ---- Geometry rows 10.. (literals; Type overwrites the LEFT() formula) ----
  const wsG=wb.getWorksheet('Geometry')!;
  rows.forEach((r,i)=>{const row=10+i;const set=(c:string,v:any)=>wsG.getCell(`${c}${row}`).value=v;
    set('C',r.label);set('D',r.us);set('E',r.ds);
    set('F',r.pipe?'Pipe':'U-channel');
    set('G',r.size);set('K',r.len);set('L',r.usIL);set('M',r.dsIL);
    set('P',r.nPipes);set('U',r.usGL);set('V',r.dsGL);});
  trimRows(wsG,10+rows.length,100);

  // ---- Catchment rows 11.. (unused rows CLEARED, not deleted:
  //      the coefficient lookup + summary tables live beside these rows) ----
  const wsC=wb.getWorksheet('Catchment')!;
  catches.forEach((c,i)=>{const row=11+i;
    wsC.getCell(`A${row}`).value=c.name;   wsC.getCell(`B${row}`).value=c.area;
    wsC.getCell(`C${row}`).value=c.surface;wsC.getCell(`D${row}`).value=c.C;   // literal C
    wsC.getCell(`E${row}`).value=c.H;      wsC.getCell(`F${row}`).value=c.L;});
  for(let row=11+catches.length;row<=41;row++)
    ['A','B','C','D','E','F'].forEach(col=>wsC.getCell(`${col}${row}`).value=null);

  // ---- Summary : delete unused rows ----
  const wsS=wb.getWorksheet('Summary'); if(wsS)trimRows(wsS,11+rows.length,101);

  // ---- per-link UC / P sheets ----
  const poolUC=wb.worksheets.filter(w=>/^UC\d+$/.test(w.name)).sort(numSort);
  const poolP =wb.worksheets.filter(w=>/^P\d+$/.test(w.name)).sort(numSort);
  const names=new Set<string>();
  for(const r of rows){
    const ws=(r.pipe?poolP:poolUC).shift()!;
    let nm=sanitize(r.label),k=1;
    while(names.has(nm))nm=sanitize(r.label).slice(0,28)+'~'+(k++);
    if(nm!==r.label)warnings.push(`Sheet name adjusted: "${r.label}" → "${nm}"`);
    names.add(nm); ws.name=nm;
    // per-link literal material/condition on the governing equation's cells
    if(r.pipe){ws.getCell('H16').value=r.material;ws.getCell('H17').value=r.condition;}
    else      {ws.getCell('H12').value=r.material;ws.getCell('H13').value=r.condition;}
    // catchment allocations B32:B37 (+ % in A, F formula rewritten when partial)
    if(r.catchAllocs.length>6)warnings.push(`${r.label}: only first 6 of ${r.catchAllocs.length} catchments fit B32:B37`);
    r.catchAllocs.slice(0,6).forEach((a,j)=>{const row=32+j;
      ws.getCell(`B${row}`).value=a.name;
      if(a.pct<100){
        ws.getCell(`A${row}`).value=a.pct;   // whole number, e.g. 60
        ws.getCell(`F${row}`).value={formula:
          `IF(ISBLANK(B${row}),"",VLOOKUP(B${row},Catchment!$A$11:$F$206,2,FALSE))*$A${row}/100`};}});
    // upstream feeders B44:B51 — only those in this workbook (INDIRECT would #REF otherwise)
    const ups=r.upstream.filter(u=>chunkLabels.has(u));
    if(ups.length<r.upstream.length)
      warnings.push(`${r.label}: upstream ${r.upstream.filter(u=>!chunkLabels.has(u)).join(', ')} in another file — enter manually`);
    if(ups.length>8)warnings.push(`${r.label}: only first 8 upstream feeders fit B44:B51`);
    ups.slice(0,8).forEach((u,j)=>ws.getCell(`B${44+j}`).value=u);
  }
  // delete unused example sheets
  [...poolUC,...poolP].forEach(ws=>wb.removeWorksheet(ws.id));
}